import { ReportSdk } from "lib-klab-sdks";

type ISODateTime = string;

type DateRange = { startDate: string; endDate?: string };

const DEFAULT_SESSIONS_PER_DAY = 2;

export const activityReportsSdkMock = {
    getAppActivitiesList: (
        employeeId = 123,
        dateRange: DateRange = buildDefaultLast30DaysRange(),
        page = 0,
        size = 20
    ): ReportSdk.AppActivityListResponse => {
        const normalizedRange = normalizeRange(dateRange);

        const all = generateAppActivityItemsForRange(normalizedRange, DEFAULT_SESSIONS_PER_DAY);

        const totalElements = all.length;
        const safePage = Math.max(0, Number(page) || 0);
        const safeSize = Math.max(1, Number(size) || 20);
        const totalPages = Math.max(1, Math.ceil(totalElements / safeSize));

        const start = safePage * safeSize;
        const end = start + safeSize;

        return {
            metadata: {
                employeeId,
                dateRange: normalizedRange,
            },
            content: all.slice(start, end),
            page: {
                page: safePage,
                size: safeSize,
                totalElements,
                totalPages,
            },
        } as ReportSdk.AppActivityListResponse;
    },

    getAppsTopWithPercentage: (
        employeeId = 123,
        dateRange: DateRange = buildDefaultLast30DaysRange(),
    ): ReportSdk.AppsTopWithPercentageResponse => {
        const normalizedRange = normalizeRange(dateRange);

        const all = generateAppActivityItemsForRange(normalizedRange, DEFAULT_SESSIONS_PER_DAY);

        const totals = new Map<string, number>();
        for (const it of all) {
            const p = String(it.processName || "").toLowerCase();
            totals.set(p, (totals.get(p) ?? 0) + (it.totalSeconds ?? 0));
        }

        const sorted = Array.from(totals.entries())
            .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
            .slice(0, 8);

        const content: ReportSdk.AppTopItemWithPercentage[] = sorted.map(
            ([processName, totalSeconds]) => ({
                processName,
                totalSeconds,
                classification: classifyProcess(processName),
            })
        );

        const totalSecondsAll = content.reduce((acc, x) => acc + (x.totalSeconds ?? 0), 0);

        return {
            metadata: {
                employeeId,
                dateRange: normalizedRange,
                totalSeconds: totalSecondsAll,
            },
            content,
        } as ReportSdk.AppsTopWithPercentageResponse;
    },
};

function normalizeRange(range: DateRange): Required<DateRange> {
    const start = parseYmd(range.startDate);
    const end = parseYmd(range.endDate ?? range.startDate);

    const s = start.getTime();
    const e = end.getTime();

    if (s <= e) return { startDate: toYmd(start), endDate: toYmd(end) };
    return { startDate: toYmd(end), endDate: toYmd(start) };
}

function generateAppActivityItemsForRange(
    dateRange: Required<DateRange>,
    sessionsPerDay = 2
): ReportSdk.AppActivityItem[] {
    const start = parseYmd(dateRange.startDate);
    const end = parseYmd(dateRange.endDate);

    const days = enumerateDaysInclusive(start, end);

    const apps = [
        {
            processName: "code.exe",
            title: "klab-micros-ui dashboard - Visual Studio Code",
            active: 60 * 60 * 2,
            idle: 60 * 10,
        },
        {
            processName: "chrome.exe",
            title: "Diseño UI/UX | Proyectos - Google Chrome",
            active: 60 * 60 * 1 + 60 * 20,
            idle: 60 * 15,
        },
        {
            processName: "msedge.exe",
            title: "Jira / Tickets - Microsoft Edge",
            active: 60 * 40,
            idle: 60 * 5,
        },
        {
            processName: "slack.exe",
            title: "Klab | Standup / coordinación - Slack",
            active: 60 * 15,
            idle: 60 * 8,
        },
        {
            processName: "spotify.exe",
            title: "Música - Spotify",
            active: 60 * 5,
            idle: 60 * 25,
        },
        {
            processName: "notion.exe",
            title: "Documentación / notas - Notion",
            active: 60 * 25,
            idle: 60 * 5,
        },
        {
            processName: "teams.exe",
            title: "Reunión - Microsoft Teams",
            active: 60 * 35,
            idle: 60 * 2,
        },
        {
            processName: "postman.exe",
            title: "API testing - Postman",
            active: 60 * 30,
            idle: 60 * 3,
        },
    ];

    const result: ReportSdk.AppActivityItem[] = [];

    for (let dIndex = 0; dIndex < days.length; dIndex++) {
        const day = days[dIndex];

        for (let s = 0; s < sessionsPerDay; s++) {
            const app = apps[(dIndex * sessionsPerDay + s) % apps.length];

            const startHour = [9, 11, 15, 18][s] ?? 9;
            const minuteJitter = (dIndex * 7 + s * 11) % 50;
            const startAt = withTime(day, startHour, minuteJitter, 0);

            const activeSeconds = jitter(app.active, dIndex, s);
            const idleSeconds = jitter(app.idle, dIndex + 3, s + 2);
            const totalSeconds = activeSeconds + idleSeconds;
            const endedAt = addSeconds(startAt, totalSeconds);

            result.push({
                processName: app.processName,
                title: app.title,
                startAt: toIsoZ(startAt) as ISODateTime,
                endedAt: toIsoZ(endedAt) as ISODateTime,
                activeSeconds,
                idleSeconds,
                totalSeconds,
            } as any);
        }
    }

    result.sort((a, b) => String(b.startAt).localeCompare(String(a.startAt)));

    return result;
}

function classifyProcess(
    processName: string
): ReportSdk.AppTopItemWithPercentage.ClassificationEnum {
    const p = String(processName || "").toLowerCase();

    if (p.includes("code") || p.includes("postman")) {
        return ReportSdk.AppTopItemWithPercentage.ClassificationEnum.Productive;
    }

    if (p.includes("chrome") || p.includes("msedge") || p.includes("edge") || p.includes("notion")) {
        return ReportSdk.AppTopItemWithPercentage.ClassificationEnum.Productive;
    }

    if (p.includes("slack") || p.includes("teams")) {
        return ReportSdk.AppTopItemWithPercentage.ClassificationEnum.Neutral;
    }

    if (p.includes("spotify")) {
        return ReportSdk.AppTopItemWithPercentage.ClassificationEnum.Unproductive;
    }

    return ReportSdk.AppTopItemWithPercentage.ClassificationEnum.Neutral;
}

function buildDefaultLast30DaysRange(): Required<DateRange> {
    const end = startOfToday();
    const start = new Date(end);
    start.setDate(start.getDate() - 29);
    return { startDate: toYmd(start), endDate: toYmd(end) };
}

function startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function parseYmd(ymd: string): Date {
    const [y, m, d] = String(ymd).split("-").map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
}

function enumerateDaysInclusive(start: Date, end: Date): Date[] {
    const res: Date[] = [];
    const cur = new Date(start);
    cur.setHours(0, 0, 0, 0);

    const last = new Date(end);
    last.setHours(0, 0, 0, 0);

    while (cur.getTime() <= last.getTime()) {
        res.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }

    return res;
}

function withTime(day: Date, hh: number, mm: number, ss: number): Date {
    const d = new Date(day);
    d.setHours(hh, mm, ss, 0);
    return d;
}

function addSeconds(date: Date, seconds: number): Date {
    return new Date(date.getTime() + seconds * 1000);
}

function toIsoZ(d: Date): string {
    return new Date(d.getTime()).toISOString();
}

function jitter(baseSeconds: number, a: number, b: number): number {
    const delta = ((a * 17 + b * 31) % 9) * 60;
    return Math.max(60, Math.floor(baseSeconds + delta));
}

function toYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}